import { computed } from 'vue';
import { CATEGORY_COLORS, useIntelligentInbox, type IntelligentCategory } from './useIntelligentInbox';

type TrendDirection = 'up' | 'down' | 'flat';

export type SectionStat = {
    label: string;
    count: number;
    color: string;
    trend: TrendDirection;
    trendValue: string;
};

export type PriorityThread = {
    id: number;
    contactName: string;
    contactInitials: string;
    timeAgo: string;
    category: IntelligentCategory;
    priorityReason: string;
    suggestedReply: string;
};

export type AiSuggestion = {
    id: number;
    title: string;
    description: string;
    icon: 'auto-reply' | 'follow-up' | 're-engagement';
    ctaLabel: string;
    count: number;
};

export type BriefItem = {
    text: string;
    count: number;
    sectionState: string;
    color: string;
};

export const useIntelligentInboxDashboard = () => {
    const { sections, categories } = useIntelligentInbox();

    const findCategory = (name: string) => categories.value.find((c) => c.displayName === name);

    const priorityThreads = computed<PriorityThread[]>(() => [
        {
            id: 1,
            contactName: 'Sarah Johnson',
            contactInitials: 'SJ',
            timeAgo: '5 min ago',
            category: findCategory('Tour Request')!,
            priorityReason:
                'Parent requested a tour for their 3-year-old after recently relocating. Mentioned needing childcare urgently to start a new job next week — high conversion likelihood.',
            suggestedReply:
                "Hi Sarah! We'd love to welcome you for a tour. We have openings in our 3-year-old program. Would tomorrow at 10am or Thursday at 2pm work for you?",
        },
        {
            id: 2,
            contactName: 'Michael Chen',
            contactInitials: 'MC',
            timeAgo: '23 min ago',
            category: findCategory('Service Issue/Complaint')!,
            priorityReason:
                'Parent has sent 3 messages over 2 weeks about a billing discrepancy with escalating frustration. Last message mentioned considering withdrawal — risk of churn.',
            suggestedReply:
                "Hi Michael, I sincerely apologize for the delay. I've escalated your billing concern to our director who will personally review and resolve this today.",
        },
        {
            id: 3,
            contactName: 'Emily Rodriguez',
            contactInitials: 'ER',
            timeAgo: '1 hour ago',
            category: findCategory('Pricing Inquiry')!,
            priorityReason:
                "Customer confirmed their tour time and then immediately asked about pricing, followed by a 'stop' command. Address the pricing inquiry and re-engage before the opt-out finalizes.",
            suggestedReply:
                "Hi Emily! We're so glad you enjoyed the tour! I'd love to walk you through our Pre-K pricing and enrollment options. Can I give you a quick call this afternoon?",
        },
    ]);

    const briefItems = computed<BriefItem[]>(() => [
        { text: 'needing action', count: 12, sectionState: 'needs-action', color: 'rose' },
        { text: 'requiring follow-up', count: 8, sectionState: 'follow-up-recommended', color: 'amber' },
        { text: 'awaiting reply', count: 15, sectionState: 'awaiting-reply', color: 'sky' },
    ]);

    return {
        priorityThreads,
        briefItems,
        sections,
        CATEGORY_COLORS,
    };
};
